import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { HeroBackdropVideo } from "@/components/common/HeroBackdropVideo";
import { AppStoreDownloadDialog } from "@/components/common/AppStoreDownloadDialog";
import { Link, useSearchParams } from "react-router-dom";
import {
  Users,
  MapPin,
  ArrowRight,
  Clock,
  IndianRupee,
  Star,
  ShieldCheck,
  Leaf,
  Sparkles,
  Search,
  Plus,
  Route as RouteIcon,
  HelpCircle,
  Bus,
  Fuel,
  Smile,
  CheckCircle2,
  Quote,
  Lock,
  UserCheck,
  Heart,
  Calendar,
  ArrowLeftRight,
  Repeat2,
} from "lucide-react";

import { ROUTE_PATHS, corridorPath } from "@/routes/routePaths";
import { getCorridorPageSeo } from "@/config/corridorSeoMeta";
import { toPublicCorridorSlug } from "@/data/corridors/corridorSeoSlugs";
import { hostedRides, waitingGuests, type HostedRide } from "@/data/sampleRoutes";
import type { CorridorDefinition, CorridorSpotIcon } from "@/types/corridor";
import { Landmark, Utensils, Mountain, Waves, Building2, TreePalm } from "lucide-react";
import { CorridorRouteGuideSection } from "@/components/corridor/CorridorRouteGuideSection";

const spotIcons: Record<CorridorSpotIcon, ComponentType<{ className?: string }>> = {
  waves: Waves,
  mountain: Mountain,
  treePalm: TreePalm,
  landmark: Landmark,
  utensils: Utensils,
  building2: Building2,
};

// Auto-contrast hook: samples the text region of the hero image to decide
// whether the overlay/text should be light-on-dark or dark-on-light.
const useAutoContrast = (src: string) => {
  const [scheme, setScheme] = useState<"light" | "dark">("light");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const w = 80;
        const h = 80;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        const sw = Math.floor(img.naturalWidth * 0.6) || img.naturalWidth;
        const sh = img.naturalHeight;
        ctx.drawImage(img, 0, 0, sw, sh, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let total = 0;
        const count = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          total += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
        }
        const avg = total / count;
        setScheme(avg < 140 ? "light" : "dark");
      } catch {
        setScheme("light");
      } finally {
        // next frame so the initial classes apply, then transition
        requestAnimationFrame(() => setReady(true));
      }
    };
  }, [src]);
  return { scheme, ready };
};

export function CorridorPageView({
  corridor,
  apiLiveRides,
}: {
  corridor: CorridorDefinition;
  apiLiveRides?: { forward: HostedRide[]; reverse: HostedRide[] };
}) {
  const heroGreenGradient =
    "radial-gradient(circle at 18% 15%, #2ea36f 0%, #1f7a5a 36%, #165845 72%, #0d3f33 100%)";

  const stats = corridor.stats;
  const CITY_A = corridor.cityA;
  const CITY_B = corridor.cityB;

  const [searchParams, setSearchParams] = useSearchParams();

  const direction: "AB" | "BA" =
    searchParams.get("dir")?.toLowerCase() === "ba" ? "BA" : "AB";
  const fromCity = direction === "AB" ? CITY_A : CITY_B;
  const toCity = direction === "AB" ? CITY_B : CITY_A;
  const fromZones = direction === "AB" ? stats.zonesA : stats.zonesB;
  const toZones = direction === "AB" ? stats.zonesB : stats.zonesA;

  const pickup = (() => {
    const p = searchParams.get("pickup");
    return p && fromZones.includes(p) ? p : fromZones[0];
  })();
  const drop = (() => {
    const d = searchParams.get("drop");
    return d && toZones.includes(d) ? d : toZones[0];
  })();
  const date = searchParams.get("date") ?? "";

  const [heroMediaFailed, setHeroMediaFailed] = useState(false);
  const [bookAppDialogOpen, setBookAppDialogOpen] = useState(false);

  useEffect(() => {
    setHeroMediaFailed(false);
  }, [corridor.slug]);

  /** Keep hero state addressable and shareable via query string. */
  useEffect(() => {
    const dirQ = searchParams.get("dir")?.toLowerCase() === "ba" ? "BA" : "AB";
    const fz = dirQ === "AB" ? stats.zonesA : stats.zonesB;
    const tz = dirQ === "AB" ? stats.zonesB : stats.zonesA;
    const p = searchParams.get("pickup");
    const d = searchParams.get("drop");
    const fixes: Record<string, string> = {};
    if (!searchParams.get("dir")) fixes.dir = "ab";
    if (!p || !fz.includes(p)) fixes.pickup = fz[0];
    if (!d || !tz.includes(d)) fixes.drop = tz[0];
    if (Object.keys(fixes).length === 0) return;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        Object.entries(fixes).forEach(([k, v]) => n.set(k, v));
        const dt = prev.get("date");
        if (dt) n.set("date", dt);
        return n;
      },
      { replace: true }
    );
  }, [corridor.slug, stats.zonesA, stats.zonesB, searchParams, setSearchParams]);

  const swapDirection = () => {
    const next = direction === "AB" ? "BA" : "AB";
    const nf = next === "AB" ? stats.zonesA : stats.zonesB;
    const nt = next === "AB" ? stats.zonesB : stats.zonesA;
    const nextParams: Record<string, string> = {
      dir: next === "BA" ? "ba" : "ab",
      pickup: nf[0],
      drop: nt[0],
    };
    const dt = searchParams.get("date");
    if (dt) nextParams.date = dt;
    setSearchParams(nextParams, { replace: true });
  };

  const heroSearchLink = useMemo(() => {
    const q = new URLSearchParams();
    q.set("fromCity", fromCity);
    q.set("toCity", toCity);
    q.set("pickup", pickup);
    q.set("drop", drop);
    if (date) q.set("date", date);
    return `${ROUTE_PATHS.FIND_RIDES}?${q.toString()}`;
  }, [fromCity, toCity, pickup, drop, date]);

  const fwdRides = useMemo(
    () =>
      apiLiveRides?.forward?.length
        ? apiLiveRides.forward
        : hostedRides.filter((r) => r.from === CITY_A && r.to === CITY_B),
    [apiLiveRides, CITY_A, CITY_B]
  );
  const revRides = useMemo(
    () =>
      apiLiveRides?.reverse?.length
        ? apiLiveRides.reverse
        : hostedRides.filter((r) => r.from === CITY_B && r.to === CITY_A),
    [apiLiveRides, CITY_A, CITY_B]
  );
  const fwdGuests = useMemo(() => waitingGuests.filter((g) => g.from === CITY_A && g.to === CITY_B), []);
  const revGuests = useMemo(() => waitingGuests.filter((g) => g.from === CITY_B && g.to === CITY_A), []);

  const liveRideCount = Math.max(fwdRides.length + revRides.length, stats.ridesToday);

  const { scheme: heroScheme, ready: heroReady } = useAutoContrast(corridor.heroImageSrc);
  const isLight = heroScheme === "light"; // true => light text on dark image

  // SEO + AEO
  useEffect(() => {
    const publicSlug = toPublicCorridorSlug(corridor.slug);
    const preset = getCorridorPageSeo(publicSlug);
    const title =
      preset?.title ?? `${CITY_A} to ${CITY_B} Carpool & Ride Sharing | Duri (Both Ways)`;
    const description =
      preset?.description ??
      `Book a carpool from ${CITY_A} to ${CITY_B} or ${CITY_B} to ${CITY_A} on Duri. Verified co-travellers, seats from ₹${stats.carpoolFare}, save up to ₹${stats.monthlySavings.toLocaleString()}/month vs cabs. Daily rides on ${stats.highway}.`;
    const url = `${window.location.origin}${corridorPath(corridor.slug)}`;
    const image = corridor.seoOgImage;

    document.title = title;

    const upsert = (selector: string, attrs: Record<string, string>) => {
      let el = document.head.querySelector(selector) as HTMLElement | null;
      if (!el) {
        el = document.createElement(selector.startsWith("link") ? "link" : "meta");
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    };

    upsert('meta[name="description"]', { name: "description", content: description });
    upsert('meta[name="robots"]', { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" });
    upsert('meta[name="author"]', { name: "author", content: "DuriApp.com" });

    // Open Graph
    upsert('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsert('meta[property="og:title"]', { property: "og:title", content: title });
    upsert('meta[property="og:description"]', { property: "og:description", content: description });
    upsert('meta[property="og:url"]', { property: "og:url", content: url });
    upsert('meta[property="og:image"]', { property: "og:image", content: image });
    upsert('meta[property="og:site_name"]', { property: "og:site_name", content: "Duri Ridesharing" });
    upsert('meta[property="og:locale"]', { property: "og:locale", content: "en_IN" });

    // Twitter
    upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:site"]', { name: "twitter:site", content: "@DuriApp" });
    upsert('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsert('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsert('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    upsert('link[rel="canonical"]', { rel: "canonical", href: url });
  }, [corridor]);

  return (
    <div className="min-h-screen bg-background pb-[calc(5.25rem+env(safe-area-inset-bottom,0px))] md:pb-0">
      <main id="main-content">
      {/* ===== Hero ===== */}
      <section
        className={`relative overflow-hidden transition-colors duration-500 ease-out ${
          isLight ? "text-white" : "text-foreground"
        }`}
        style={{ backgroundImage: heroGreenGradient, backgroundColor: "#165845" }}
      >
        <div
          className="absolute inset-0"
          style={{ backgroundImage: heroGreenGradient, backgroundColor: "#165845" }}
        >
          {!heroMediaFailed && corridor.heroVideoSrc ? (
            <>
              <HeroBackdropVideo
                src={corridor.heroVideoSrc}
                objectPosition="center 42%"
                className="scale-105 brightness-[1.06] saturate-[1.12] contrast-[1.03]"
                onError={() => setHeroMediaFailed(true)}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/20 to-black/35" aria-hidden />
            </>
          ) : !heroMediaFailed && !corridor.heroVideoSrc ? (
            <img
              src={corridor.heroImageSrc}
              alt={corridor.heroImageAlt}
              width={1920}
              height={1080}
              fetchpriority="high"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover [image-rendering:auto]"
              style={{ objectPosition: "center 48%" }}
              onError={() => setHeroMediaFailed(true)}
            />
          ) : null}
        </div>
        {/* Subtle brand tint — kept light so the road video stays visible */}
        <div className="absolute inset-0 bg-emerald-950/15" aria-hidden />
        {/* No overlay — image stays 100% pure. Text legibility comes from
            the dual-layer text-shadow + paint-order stroke applied below. */}
        <div
          className="container relative min-w-0 min-h-[32vh] pb-5 pt-5 sm:min-h-[36vh] sm:pt-8 md:min-h-[38vh] md:pb-8 md:pt-12 lg:min-h-[400px] lg:pb-10 lg:pt-[7rem]"
        >
          <div className="grid min-w-0 gap-6 md:grid-cols-5 md:items-end md:gap-8 lg:gap-10">
            <div
              className="min-w-0 md:col-span-3"
              style={{
                // Dual-layer text shadow + paint-order stroke for crisp legibility
                // on ANY background, regardless of overlay strength.
                textShadow: isLight
                  ? "0 1px 2px rgba(0,0,0,0.85), 0 2px 14px rgba(0,0,0,0.55), 0 0 1px rgba(0,0,0,0.9)"
                  : "0 1px 2px rgba(255,255,255,0.85), 0 2px 14px rgba(255,255,255,0.55)",
                WebkitTextStroke: isLight ? "0.4px rgba(0,0,0,0.35)" : "0.4px rgba(255,255,255,0.35)",
                paintOrder: "stroke fill",
                transition: "text-shadow 600ms ease-out, color 500ms ease-out, -webkit-text-stroke-color 500ms ease-out",
                opacity: heroReady ? 1 : 0,
              }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm ring-1 ring-white/20">
                <Sparkles className="h-3.5 w-3.5 text-accent" /> {liveRideCount} rides today • both directions
              </span>
              <h1 className="mt-4 text-3xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.65), 0 1px 2px rgba(0,0,0,0.85)" }}>
                {CITY_A} <span>↔</span> {CITY_B}
                <span className="mt-2 block text-lg font-semibold text-white sm:text-xl md:text-2xl lg:text-3xl" style={{ textShadow: "0 2px 10px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.85)" }}>Carpool — Save Money & Travel Smart</span>
              </h1>

              {/* Direction chips */}
              <div className="mt-5 inline-flex max-w-full flex-wrap rounded-full border border-white/30 bg-black/35 p-1 backdrop-blur-sm">
                <button
                  type="button"
                  onClick={() => direction !== "AB" && swapDirection()}
                  className={`rounded-full px-3 py-2 text-[11px] font-semibold transition-colors duration-300 sm:px-4 sm:text-xs ${
                    direction === "AB"
                      ? "bg-white text-primary"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {CITY_A} → {CITY_B}
                </button>
                <button
                  type="button"
                  onClick={() => direction !== "BA" && swapDirection()}
                  className={`rounded-full px-3 py-2 text-[11px] font-semibold transition-colors duration-300 sm:px-4 sm:text-xs ${
                    direction === "BA"
                      ? "bg-white text-primary"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {CITY_B} → {CITY_A}
                </button>
              </div>

              {/* Trust strip */}
              <div className="mt-5 grid grid-cols-2 gap-2 text-[11px] sm:flex sm:flex-wrap sm:gap-x-3 sm:text-sm">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1 text-white backdrop-blur-sm ring-1 ring-white/20"><ShieldCheck className="h-4 w-4" /> ID-verified</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1 text-white backdrop-blur-sm ring-1 ring-white/20"><Star className="h-4 w-4 fill-current text-accent" /> {stats.avgRating}/5 rating</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1 text-white backdrop-blur-sm ring-1 ring-white/20"><CheckCircle2 className="h-4 w-4" /> {stats.completedRides.toLocaleString()}+ rides done</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-black/40 px-3 py-1 text-white backdrop-blur-sm ring-1 ring-white/20"><Lock className="h-4 w-4" /> Secure booking</span>
              </div>
            </div>

            {/* Search card */}
            <div className="mt-4 w-full min-w-0 max-w-full md:col-span-2 md:mt-0 md:max-w-[19rem] md:justify-self-end lg:max-w-[20rem]">
              <div className="min-w-0 overflow-hidden rounded-2xl border border-border/40 bg-background/95 p-4 text-foreground shadow-lg sm:p-5">
                <div className="flex min-w-0 items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Find your ride</h2>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">{fromCity} → {toCity}</p>
                  </div>
                  <button
                    type="button"
                    onClick={swapDirection}
                    title="Swap direction"
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20"
                  >
                    <Repeat2 className="h-3.5 w-3.5" /> Swap
                  </button>
                </div>
                <div className="mt-3 min-w-0 space-y-2.5">
                  <div className="relative min-w-0">
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-primary" />
                    <select
                      value={pickup}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchParams(
                          (prev) => {
                            const n = new URLSearchParams(prev);
                            n.set("pickup", v);
                            n.set("dir", direction === "BA" ? "ba" : "ab");
                            const dr = prev.get("drop");
                            if (dr && toZones.includes(dr)) n.set("drop", dr);
                            else n.set("drop", drop);
                            const dt = prev.get("date");
                            if (dt) n.set("date", dt);
                            return n;
                          },
                          { replace: true }
                        );
                      }}
                      className="box-border w-full min-w-0 max-w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-2 text-sm outline-none focus:ring-2 focus:ring-ring sm:pr-3"
                    >
                      {fromZones.map((z) => <option key={z}>{z}</option>)}
                    </select>
                  </div>
                  <div className="relative min-w-0">
                    <MapPin className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-accent" />
                    <select
                      value={drop}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchParams(
                          (prev) => {
                            const n = new URLSearchParams(prev);
                            n.set("drop", v);
                            n.set("dir", direction === "BA" ? "ba" : "ab");
                            const pu = prev.get("pickup");
                            if (pu && fromZones.includes(pu)) n.set("pickup", pu);
                            else n.set("pickup", pickup);
                            const dt = prev.get("date");
                            if (dt) n.set("date", dt);
                            return n;
                          },
                          { replace: true }
                        );
                      }}
                      className="box-border w-full min-w-0 max-w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-2 text-sm outline-none focus:ring-2 focus:ring-ring sm:pr-3"
                    >
                      {toZones.map((z) => <option key={z}>{z}</option>)}
                    </select>
                  </div>
                  <div className="relative min-w-0">
                    <Calendar className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchParams(
                          (prev) => {
                            const n = new URLSearchParams(prev);
                            if (v) n.set("date", v);
                            else n.delete("date");
                            n.set("dir", direction === "BA" ? "ba" : "ab");
                            n.set("pickup", pickup);
                            n.set("drop", drop);
                            return n;
                          },
                          { replace: true }
                        );
                      }}
                      className="corridor-hero-date box-border block w-full min-w-0 max-w-full appearance-none rounded-lg border border-input bg-background py-2.5 pl-9 pr-2 text-sm outline-none focus:ring-2 focus:ring-ring sm:pr-3"
                    />
                  </div>
                  <Link
                    to={heroSearchLink}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <Search className="h-4 w-4" /> Find a Ride
                  </Link>
                  <a
                    href="https://duriapp.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted"
                  >
                    <Plus className="h-4 w-4" /> Offer a Ride
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Problem → Solution ===== */}
      <section className="container py-10 sm:py-12 lg:py-14">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">{corridor.problemSolution.headline}</h2>
          <p className="mt-3 text-muted-foreground">{corridor.problemSolution.sub}</p>
        </div>
        <div className="mx-auto mt-10 grid max-w-5xl gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-6">
            <h3 className="text-lg font-semibold text-foreground">The old way</h3>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3"><Fuel className="h-5 w-5 shrink-0 text-destructive" /> ₹2,200+ in fuel & tolls — alone</li>
              <li className="flex gap-3"><Bus className="h-5 w-5 shrink-0 text-destructive" /> Crowded AC buses, fixed pickup points</li>
              <li className="flex gap-3"><Clock className="h-5 w-5 shrink-0 text-destructive" /> Long waits, missed connections</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6">
            <h3 className="text-lg font-semibold text-foreground">The Duri way</h3>
            <ul className="mt-4 space-y-3 text-sm text-foreground">
              <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" /> Split costs — pay only ₹{stats.carpoolFare}/seat</li>
              <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" /> Door-to-door pickups in your area</li>
              <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" /> Verified hosts, ratings & live tracking</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== Benefits ===== */}
      <section className="bg-muted/40 py-10 sm:py-12 lg:py-14">
        <div className="container">
          <h2 className="text-center text-2xl font-bold text-foreground md:text-3xl">Why commuters love Duri on this route</h2>
          <div className="mx-auto mt-10 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <Benefit icon={<IndianRupee className="h-5 w-5" />} title={`Save ₹${stats.monthlySavings.toLocaleString()}/month`} text="Daily commuters typically cut travel costs by 60% vs cabs." />
            <Benefit icon={<Leaf className="h-5 w-5" />} title="Lower emissions" text="Each shared seat removes ~25 kg of CO₂ per round trip." />
            <Benefit icon={<Smile className="h-5 w-5" />} title="Comfortable rides" text="Sedans & SUVs only — no jam-packed buses or sketchy shuttles." />
            <Benefit icon={<Heart className="h-5 w-5" />} title="Meet cool people" text="Travel with students, IT pros & families heading the same way." />
          </div>
        </div>
      </section>

      {/* ===== How Duri Works  ===== */}
      <section id="corridor-booking-steps" className="container py-10 sm:py-12 lg:py-14 scroll-mt-28">
        <h2 className="text-center text-2xl font-bold text-foreground md:text-3xl">Book a ride in 3 simple steps</h2>
        <div className="mx-auto mt-10 grid max-w-5xl gap-6 md:grid-cols-3">
          <Step n={1} icon={<Search className="h-5 w-5" />} title="Search a ride" text={`Pick your direction (${CITY_A} → ${CITY_B} or ${CITY_B} → ${CITY_A}), pickup, drop and date.`} />
          <Step n={2} icon={<UserCheck className="h-5 w-5" />} title="Book a seat" text="Pick a verified host, see ratings, confirm in under a minute." />
          <Step n={3} icon={<ShieldCheck className="h-5 w-5" />} title="Travel safely" text="Track the ride live, chat in-app, rate at the end." />
        </div>
      </section>

      {/* ===== Route-Specific Content (rich guide when available) ===== */}
      {corridor.routeGuide ? (
        <CorridorRouteGuideSection corridor={corridor} />
      ) : (
        <section className="bg-muted/40 py-10 sm:py-12 lg:py-14">
          <div className="container">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">{CITY_A} ↔ {CITY_B} — everything you need to know</h2>
            <p className="mt-2 max-w-3xl text-muted-foreground">
              One of India's busiest inter-city corridors, served daily in <strong>both directions</strong> — {CITY_A} → {CITY_B} and {CITY_B} → {CITY_A} — with {stats.travellers.toLocaleString()}+ travellers booking through Duri so far.
            </p>

            <div className="mt-8 grid gap-6 lg:grid-cols-3">
              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground"><RouteIcon className="h-5 w-5 text-primary" /> Route at a glance</h3>
                <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                  <li><strong className="text-foreground">Distance:</strong> {stats.distanceKm} km via {stats.highway}</li>
                  <li><strong className="text-foreground">Drive time:</strong> {stats.driveTime}</li>
                  <li><strong className="text-foreground">Best departure:</strong> 5–7 AM or 9 PM</li>
                  <li><strong className="text-foreground">Tolls:</strong> 3 plazas, ~₹350 one-way</li>
                </ul>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground"><ArrowLeftRight className="h-5 w-5 text-primary" /> Pickup & drop zones (both ways)</h3>
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">{CITY_A} stops</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {stats.zonesA.map((z) => (
                      <span key={z} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{z}</span>
                    ))}
                  </div>
                  <p className="mt-4 text-xs font-semibold uppercase text-muted-foreground">{CITY_B} stops</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {stats.zonesB.map((z) => (
                      <span key={z} className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">{z}</span>
                    ))}
                  </div>
                  <p className="mt-3 text-[11px] text-muted-foreground">Works as pickup or drop depending on your direction.</p>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-card p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground"><IndianRupee className="h-5 w-5 text-primary" /> Cost comparison</h3>
                <div className="mt-4 space-y-3">
                  <FareRow label="Private cab" amount={stats.cabFare} />
                  <FareRow label="AC bus" amount={stats.busFare} />
                  <FareRow label="Duri carpool" amount={stats.carpoolFare} highlight />
                </div>
                <p className="mt-4 text-xs text-muted-foreground">Per-person, one way. Live fares in-app.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== Local life & Weekend Escapes ===== */}
      <section className="container py-10 sm:py-12 lg:py-16">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{corridor.localLife.eyebrow}</span>
            <h2 className="mt-2 text-2xl font-bold text-foreground md:text-3xl">{corridor.localLife.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{corridor.localLife.intro}</p>
          </div>
        </div>

        {/* Editorial grid — typography-first; icons from corridor data */}
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {corridor.spots.map((s) => {
            const Icon = spotIcons[s.icon];
            return (
              <Spot
                key={`${s.title}-${s.meta}`}
                icon={Icon}
                title={s.title}
                meta={s.meta}
                tag={s.tag}
                text={s.text}
                featured={s.featured}
              />
            );
          })}
        </div>
      </section>

      {/* ===== Happenings strip ===== */}
      <section className="bg-muted/40 py-10 sm:py-12">
        <div className="container">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">This season</span>
          </div>
          <h2 className="mt-2 text-2xl font-bold text-foreground md:text-3xl">Happenings worth riding for</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {corridor.happenings.map((h) => (
              <Happening key={h.title} when={h.when} title={h.title} where={h.where} text={h.text} />
            ))}
          </div>
        </div>
      </section>

      {/* ===== Live rides ===== */}
      <section className="container py-10 sm:py-12 lg:py-14">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">{CITY_A} → {CITY_B} — live rides</h2>
            <p className="mt-1 text-sm text-muted-foreground">{liveRideCount} rides available today. Book before they fill up.</p>
          </div>
          <Link to={ROUTE_PATHS.FIND_RIDES} className="hidden items-center gap-1 text-sm font-semibold text-primary hover:underline sm:inline-flex">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <RideGrid rides={fwdRides.slice(0, 6)} onBookClick={() => setBookAppDialogOpen(true)} />
      </section>

      {/* ===== Mid CTA ===== */}
      <section className="container px-4 pb-10 sm:px-6 sm:pb-12 lg:pb-14">
        <div className="rounded-2xl bg-host-gradient p-6 text-center text-primary-foreground sm:p-8 md:p-10">
          <h2 className="text-2xl font-bold md:text-3xl">{corridor.midCta.title}</h2>
          <p className="mx-auto mt-2 max-w-xl text-primary-foreground/90">{corridor.midCta.subtitle}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to={ROUTE_PATHS.FIND_RIDES} className="inline-flex items-center gap-2 rounded-lg bg-primary-foreground px-5 py-3 text-sm font-semibold text-primary shadow-sm hover:scale-[1.02]">
              <Search className="h-4 w-4" /> Find a Ride
            </Link>
            <a href="https://duriapp.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/20">
              <Plus className="h-4 w-4" /> Offer a Ride
            </a>
          </div>
        </div>
      </section>

      {/* ===== Reverse rides ===== */}
      <section className="bg-muted/40 py-10 sm:py-12 lg:py-14">
        <div className="container">
          <h2 className="text-2xl font-bold text-foreground md:text-3xl">{CITY_B} → {CITY_A} — return rides</h2>
          <p className="mt-1 text-sm text-muted-foreground">Plenty of options every evening and on Sunday nights.</p>
          <RideGrid rides={revRides.slice(0, 6)} onBookClick={() => setBookAppDialogOpen(true)} />
        </div>
      </section>

      {/* ===== Social proof / Testimonials ===== */}
      <section className="container py-10 sm:py-12 lg:py-14">
        <h2 className="text-center text-2xl font-bold text-foreground md:text-3xl">Trusted by daily commuters & weekend travellers</h2>
        <div className="mx-auto mt-4 flex max-w-3xl flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-2"><Star className="h-4 w-4 fill-current text-amber-500" /> {stats.avgRating}/5 average rating</span>
          <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> {stats.completedRides.toLocaleString()}+ rides completed</span>
          <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> {stats.travellers.toLocaleString()}+ travellers</span>
        </div>
        <div className="mx-auto mt-10 grid max-w-6xl gap-5 md:grid-cols-3">
          {corridor.testimonials.map((t) => (
            <Testimonial key={t.name} name={t.name} role={t.role} rating={t.rating} text={t.text} />
          ))}
        </div>
      </section>

      {/* ===== Safety & Trust (generic fallback when no route guide) ===== */}
      {!corridor.routeGuide ? (
        <section className="bg-muted/40 py-10 sm:py-12 lg:py-14">
          <div className="container">
            <h2 className="text-center text-2xl font-bold text-foreground md:text-3xl">Your safety is non-negotiable</h2>
            <div className="mx-auto mt-10 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <Trust icon={<UserCheck className="h-5 w-5" />} title="Verified profiles" text="Every host & guest is Aadhaar-verified." />
              <Trust icon={<Star className="h-5 w-5" />} title="Ratings & reviews" text="See real reviews before you book." />
              <Trust icon={<Lock className="h-5 w-5" />} title="Secure booking" text="In-app payment & verified vehicles only." />
              <Trust icon={<Heart className="h-5 w-5" />} title="Women safety" text="Women-only & women-preferred filters." />
            </div>
          </div>
        </section>
      ) : null}

      {/* ===== FAQs ===== */}
      <section className="container py-10 sm:py-12 lg:py-14">
        <div className="mx-auto max-w-3xl">
          <div className="flex items-center justify-center gap-2">
            <HelpCircle className="h-5 w-5 text-primary" />
            <h2 className="text-center text-2xl font-bold text-foreground md:text-3xl">Frequently asked questions</h2>
          </div>
          <div className="mt-8 space-y-3">
            {corridor.faqs.map((f) => (
              <details key={f.q} className="group rounded-xl border border-border bg-card p-5 open:shadow-md">
                <summary className="flex cursor-pointer items-start justify-between gap-3 text-base font-semibold text-foreground">
                  {f.q}
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="container px-4 pb-10 sm:px-6 sm:pb-12 lg:pb-14">
        <div className="rounded-2xl bg-host-gradient p-6 text-primary-foreground sm:p-8 md:p-12">
          <div className="grid gap-6 md:grid-cols-2 md:items-center">
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold md:text-3xl">Your next {CITY_A}–{CITY_B} ride is one tap away</h2>
              <p className="mt-2 text-primary-foreground/90">Join {stats.travellers.toLocaleString()}+ travellers already saving money & time on Duri.</p>
              {(fwdGuests.length + revGuests.length) > 0 && (
                <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-medium backdrop-blur">
                  <Users className="h-3.5 w-3.5" /> {fwdGuests.length + revGuests.length} guest{(fwdGuests.length + revGuests.length) > 1 ? "s" : ""} waiting on this corridor right now
                </p>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-3 md:justify-end">
              <Link to={ROUTE_PATHS.FIND_RIDES} className="inline-flex min-h-[44px] min-w-[140px] items-center justify-center gap-2 rounded-lg bg-primary-foreground px-5 py-3 text-sm font-semibold text-primary shadow-sm hover:scale-[1.02]">
                <Search className="h-4 w-4" /> Find a Ride
              </Link>
              <a href="https://duriapp.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/20">
                <Plus className="h-4 w-4" /> Offer a Ride
              </a>
            </div>
          </div>
        </div>
      </section>

      </main>

      {/* ===== Sticky mobile CTA ===== */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-2xl backdrop-blur md:hidden">
        <div className="flex gap-2">
          <Link
            to={heroSearchLink}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground"
          >
            <Search className="h-4 w-4" /> Find Ride
          </Link>
          <a href="https://duriapp.com" target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground">
            <Plus className="h-4 w-4" /> Offer
          </a>
        </div>
      </div>

      <AppStoreDownloadDialog
        open={bookAppDialogOpen}
        onOpenChange={setBookAppDialogOpen}
        title="Download our app to book or host rides"
      />
    </div>
  );
}

// ====== Sub-components ======
const Benefit = ({ icon, title, text }: { icon: ReactNode; title: string; text: string }) => (
  <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm transition-shadow hover:shadow-md">
    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">{icon}</div>
    <h3 className="mt-4 text-base font-semibold text-foreground">{title}</h3>
    <p className="mt-2 text-sm text-muted-foreground">{text}</p>
  </div>
);

const Step = ({ n, icon, title, text }: { n: number; icon: ReactNode; title: string; text: string }) => (
  <div className="relative rounded-2xl border border-border bg-card p-6 shadow-sm">
    <span className="absolute -top-3 left-6 rounded-full bg-primary px-3 py-0.5 text-xs font-bold text-primary-foreground">Step {n}</span>
    <div className="mt-2 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</div>
    <h3 className="mt-4 text-lg font-semibold text-foreground">{title}</h3>
    <p className="mt-2 text-sm text-muted-foreground">{text}</p>
  </div>
);

const FareRow = ({ label, amount, highlight }: { label: string; amount: number; highlight?: boolean }) => (
  <div className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${highlight ? "bg-primary/10 font-semibold text-primary" : "bg-muted text-foreground"}`}>
    <span>{label}</span>
    <span>₹{amount.toLocaleString()}</span>
  </div>
);

const Testimonial = ({ name, role, rating, text }: { name: string; role: string; rating: number; text: string }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
    <Quote className="h-6 w-6 text-primary/40" />
    <p className="mt-3 text-sm text-foreground">"{text}"</p>
    <div className="mt-4 flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold text-foreground">{name}</div>
        <div className="text-xs text-muted-foreground">{role}</div>
      </div>
      <div className="flex">
        {Array.from({ length: rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
      </div>
    </div>
  </div>
);

const Trust = ({ icon, title, text }: { icon: ReactNode; title: string; text: string }) => (
  <div className="rounded-2xl border border-border bg-card p-6">
    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">{icon}</div>
    <h3 className="mt-4 text-base font-semibold text-foreground">{title}</h3>
    <p className="mt-2 text-sm text-muted-foreground">{text}</p>
  </div>
);

const RideGrid = ({ rides, onBookClick }: { rides: HostedRide[]; onBookClick: () => void }) => {
  if (rides.length === 0) {
    return (
      <div className="mt-6 rounded-xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
        No rides loaded right now. Open the Duri app for real-time availability.
      </div>
    );
  }
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {rides.map((r) => (
        <div key={r.id} className="rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <MapPin className="h-4 w-4 text-primary" />
              {r.from} → {r.to}
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
              <Star className="h-3 w-3 fill-current" /> {r.rating.toFixed(1)}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{r.date}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{r.time}</span>
            <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{r.seatsAvailable} seats</span>
            <span>{r.vehicleType}</span>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
            <span className="text-lg font-bold text-foreground">₹{r.pricePerSeat}<span className="text-xs font-normal text-muted-foreground">/seat</span></span>
            <button
              type="button"
              onClick={onBookClick}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              Book <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const Spot = ({
  icon: Icon,
  title,
  meta,
  tag,
  text,
  featured,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  meta: string;
  tag: string;
  text: string;
  featured?: boolean;
}) => (
  <article
    className={`group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md ${
      featured ? "md:row-span-1 bg-gradient-to-br from-secondary/60 to-card" : ""
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
        <Icon className="h-5 w-5" />
      </div>
      <span className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {tag}
      </span>
    </div>
    <h3 className="mt-5 text-lg font-semibold leading-tight text-foreground">{title}</h3>
    <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
      <MapPin className="h-3 w-3" /> {meta}
    </p>
    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{text}</p>
  </article>
);

const Happening = ({ when, title, where, text }: { when: string; title: string; where: string; text: string }) => (
  <div className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md">
    <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary">
      <Calendar className="h-3 w-3" /> {when}
    </div>
    <h3 className="mt-3 text-base font-semibold text-foreground">{title}</h3>
    <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-muted-foreground">
      <MapPin className="h-3 w-3" /> {where}
    </p>
    <p className="mt-3 text-sm text-muted-foreground">{text}</p>
  </div>
);

